import { useRef, useEffect, memo } from 'react';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Checkbox from '@mui/material/Checkbox';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import InputAdornment from '@mui/material/InputAdornment';
//import { useFilePicker } from 'use-file-picker';

//import {
//  FileAmountLimitValidator,
//  FileTypeValidator,
//  FileSizeValidator,
//  //ImageDimensionsValidator,
//} from 'use-file-picker/validators';

import AutocompleteInput from 'app/components/AutocompleteInput';
import DateTimePicker from 'app/components/DateTimePicker';
import ErrorModal from 'app/components/ErrorModal';
import useForm from 'app/hooks/useForm';
import FilePicker from 'app/components/FilePicker';
import classes from './classes.module.scss';

import {
  getCurrencySign
} from 'shared/services/PriceService';

const formatHelperText = (helperText, meta) => {
  // parse and replace variables enclosed in {}
  return helperText.replace(/{([^}]+)}/g, (match, key) => {
    let value = meta[key];

    // if variable isn't in the meta object then skip
    if (!value) {
      return match;
    }

    // if value is an array then print as a CSV line
    if (Array.isArray(value)) {
      value = value.join(', ');
    }

    // convert value to string if needed
    if (typeof value !== 'string') {
      value = JSON.stringify(value);
    }

    return value;
  });
}

const SelectField = props => {
  const {
    inputProps,
    options,
    hideNoneOption,
    form,
    field
  } = props;

  const {
    getFieldValue,
    setFieldValue
  } = form;

  // track whether the value is passed via the inputProps.
  // if it's undefined then we'll want to auto-select the first option.
  // otherwise null can be considered a defined value / selected option.
  // note - having trouble handling null values at the moment,
  // relying on -999 instead for now
  let valueSet = inputProps.value !== undefined;

  // if the value isn't passed in the inputProps then check Redux
  if (!valueSet) {
    inputProps.value = getFieldValue(field);
  }

  let inputOptions = [].concat(options);

  // add none option
  if (!hideNoneOption) {
    inputOptions.unshift({ value: -999, label: '- None -' });
  }

  // if no option is selected then select the first one.
  // 0 should be considered as a selected value
  let autoSelect = !valueSet && inputProps.value == null && !!inputOptions.length;
  if (autoSelect) {
    inputProps.value = inputOptions[0].value;
  }
  useEffect(() => {
    if (autoSelect) {
      setFieldValue(field, inputProps.value);
    }
  });

  // find selected option.
  // if none is selected then assume the first one is (see autoSelect above)
  let selectedOption = inputOptions.find(opt => opt.value === inputProps.value) || inputOptions[0];

  if (!inputProps.helperText) {
    inputProps.helperText = selectedOption?.helperText;
  }

  if (inputProps.helperText && selectedOption?.meta) {
    inputProps.helperText = formatHelperText(inputProps.helperText, selectedOption.meta);
  }

  return (
    <TextField {...inputProps} select>
      {inputOptions.map((option) => (
        <MenuItem key={option.value} value={option.value}>
          {option.label}
        </MenuItem>
      ))}
    </TextField>
  );
}

const AutocompleteField = props => {
  const {
    inputProps,
    field,
    form,
    contextId
  } = props;

  const {
    getOptions,
    isOptionEqualToValue,
    multiple,
    groupBy
  } = field;

  const {
    onChange,
    getFieldValue
  } = form;

  // ref can't be passed to a function component
  delete inputProps.ref;

  // setup controlled input
  inputProps.value = getFieldValue(field);
  delete inputProps.defaultValue;

  // fix input styling to match other inputs
  let sx = {
    '.MuiInputBase-input': {
      height: '2.75rem !important',
      paddingLeft: '1rem !important',
    },
    margin: '16px 0px 0px'
  }

  return (
    <AutocompleteInput
      {...inputProps}
      getInputOptions={getOptions}
      onChange={(event, selectedOption) => onChange(field, event, selectedOption?.value, selectedOption)}
      sx={sx}
      contextId={contextId}
      isOptionEqualToValue={isOptionEqualToValue}
      multiple={multiple}
      groupBy={groupBy}
    />
  );
}

const CheckboxField = props => {
  const {
    inputProps
  } = props;

  const {
    label
  } = inputProps;

  // these props aren't accepted by Checkbox
  delete inputProps.helperText;
  delete inputProps.fullWidth;

  return (
    <FormGroup>
      <FormControlLabel control={<Checkbox {...inputProps} />} label={label} />
    </FormGroup>
  );
}

const CurrencyField = props => {
  const {
    inputProps,
    currency
  } = props;

  let symbol = getCurrencySign(currency);

  return (
    <TextField
      {...inputProps}
      step={0.1}
      style={inputProps.sx}
      type="number"
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            {symbol}
          </InputAdornment>
        ),
      }}
    />
  );
}

const DateTimeField = props => {
  const {
    inputProps,
    dateLabel,
    dateHelperText,
    timeLabel,
    timeHelperText,
    field,
    form
  } = props;

  const {
    getFieldValue
  } = form;

  inputProps.dateLabel = dateLabel; // label can also be used
  inputProps.dateHelperText = dateHelperText; // or helperText
  inputProps.timeLabel = timeLabel;
  inputProps.timeHelperText = timeHelperText;

  // ref can't be passed to a function component
  delete inputProps.ref;

  // setup controlled input
  inputProps.value = getFieldValue(field);

  return <DateTimePicker {...inputProps} />;
}

const InlineField = props => {
  const {
    field,
    fieldKey,
    form
  } = props;

  return (
    <span>
      {field.fields.map((f, i) => {
        let key = `${fieldKey}-${i}`;
        return <FieldInput {...{ field: f, key: key, fieldKey: key, form }} />;
      })}
    </span>
  );
}

const SectionField = props => {
  const {
    field,
    fieldKey,
    form,
    index
  } = props;

  let topDivider = null;
  if (index !== 0) {
    topDivider = <hr />;
  }

  return (
    <div>
      {topDivider}
      <br />
      <p><b>{field.label}</b></p>
      <p>{field.message}</p>
      <div>
        {field.fields.map((f, i) => {
          let key = `${fieldKey}-${i}`;
          return <FieldInput {...{ field: f, key: key, fieldKey: key, form }} />
        })}
      </div>
      <br />
    </div>
  );
}

const FileField = props => {
  const {
    inputProps,
    field,
    form
  } = props;

  const {
    multiple
  } = field;

  const onFilesSelected = files => {
    form.onChange(field, null, files);
  }

  // no value will be seeded
  delete inputProps.value;
  delete inputProps.defaultValue;

  let fileTypes = "image/*";
  if (field.allowVideo === true) {
    fileTypes += ",video/*"
  }

  return <FilePicker accept={fileTypes} multiple={multiple} inputProps={inputProps} onFilesSelected={onFilesSelected} />;

  //const {
  //  openFilePicker,
  //  filesContent,
  //  //loading,
  //  //errors
  //} = useFilePicker({
  //  //readAs: 'DataURL',
  //  readFilesContent: false,
  //  accept: 'image/*',
  //  multiple: true,
  //  validators: [
  //    //new FileAmountLimitValidator({ max: 1 }),
  //    new FileTypeValidator(['jpg', 'png']),
  //    new FileSizeValidator({ maxFileSize: 50 * 1024 * 1024 /* 50 MB */ }),
  //    //new ImageDimensionsValidator({
  //    //  maxHeight: 900, // in pixels
  //    //  maxWidth: 1600,
  //    //  minHeight: 600,
  //    //  minWidth: 768,
  //    //}),
  //  ],
  //  onFilesRejected: ({ errors }) => {
  //    let error = errors.map(err => `${err.name}: ${err.reason}`).join("\n");
  //    form.setFormError(error)
  //  },
  //  onFilesSuccessfullySelected: ({ plainFiles, filesContent }) => {
  //    form.onChange(field, null, plainFiles);
  //  }
  //});

  //// setup dummy value to animate TextField label
  //inputProps.value = filesContent?.length ? ' ' : '';

  //let previews = filesContent?.slice(0, 10).map((file, index) => (
  //  <img key={`${inputProps.key}-${index}`} alt={file.name} src={file.content}></img>
  //));

  //return (
  //  <div className={classes.filePicker}>
  //    <TextField {...inputProps} />
  //    <div className={classes.filePickerPreview} onClick={openFilePicker}>
  //      {previews}
  //    </div>
  //  </div>
  //);
}

const FieldInput = props => {
  const {
    field,
    fieldKey,
    form,
    index
  } = props;

  const {
    label,
    options,
    currency,
    hideNoneOption,
    helperText,
    min,
    max,
    step,
    sx,
    onClick,
    dateLabel,
    dateHelperText,
    timeLabel,
    timeHelperText,
    visible,
    contextId
  } = field;

  const {
    getFieldValue,
    getFieldError,
    onChange,
    fields
  } = form;

  let value = getFieldValue(field);
  let disabled = field.disabled;
  let type = field.type;

  if (disabled) {
    if ({}.toString.call(disabled) === '[object Function]') {
      disabled = disabled(fields);
      if (disabled) {
        value = null;
      }
    }
  }

  if (type) {
    if ({}.toString.call(type) === '[object Function]') {
      type = type(fields);
    }
  }

  // set input value via ref.
  // the regular "controlled input" method via the "value" prop lags bad.
  // so doing "semi-controlled" here, the input is "uncontrolled" for
  // half a second until its value is set via a ref.
  // should have the performance of an uncontrolled input
  const inputRef = useRef();
  useEffect(() => {
    // don't do this for file inputs (currently these have custom handling)
    if (type === 'file') {
      return;
    }

    let input = inputRef.current?.querySelector('input');
    if (input) {
      input.value = value;
    }
  });

  // hide field based on optional visible callback
  if (visible && !visible(fields)) {
    return null;
  }

  const inputProps = {
    ref: inputRef,
    key: fieldKey,
    label,
    defaultValue: value,
    onChange: e => onChange(field, e),
    margin: 'normal',
    variant: 'outlined',
    disabled,
    min,
    max,
    step,
    sx,
    onClick
  }

  // helperText can be a string or a function that takes the fields state
  let inputHelperText;
  if (helperText) {
    if (typeof helperText === 'string') {
      inputHelperText = helperText;
    } else {
      inputHelperText = helperText(fields);
    }
  }

  // currency can be a string or a function that takes the fields state
  let currencyText;
  if (currency) {
    if (typeof currency === 'string') {
      currencyText = currency;
    } else {
      currencyText = currency(fields);
    }
  }

  // show field error if set.
  // error message will become the helperText (will temporarily override)
  let error = getFieldError(field);
  if (error) {
    inputProps.error = true;

    if (typeof error === 'string') {
      inputProps.helperText = error;
    } else {
      inputProps.helperText = inputHelperText;
    }
  } else {
    inputProps.helperText = inputHelperText;
  }

  // set input to fullWidth unless sx prop is passed
  if (!inputProps.sx) {
    inputProps.fullWidth = true;
  }

  let input = null;

  switch (type) {
    case 'text':
    case 'number':
    case 'email':
    case 'tel':
    case 'password':
      input = <TextField {...inputProps} type={type} />;
      break;
    case 'textarea':
      input = <TextField {...inputProps} multiline />;
      break;
    case 'select':
      input = <SelectField {...{ inputProps, options, hideNoneOption, field, form }} />;
      break;
    case 'autocomplete':
      input = <AutocompleteField {...{ inputProps, field, form, contextId }} />;
      break;
    case 'checkbox':
      inputProps.defaultChecked = field.value;
      input = <CheckboxField {...{ inputProps }} />;
      break;
    case 'currency':
      input = <CurrencyField {...{ inputProps, currency: currencyText }} />;
      break;
    case 'datetime':
      input = <DateTimeField {...{ inputProps, dateLabel, dateHelperText, timeLabel, timeHelperText, field, form }} />;
      break;
    case 'button':
      input = <Button {...inputProps}>{label}</Button>;
      break;
    case 'inline':
      input = <InlineField {...{ field, fieldKey, form }} />;
      break;
    case 'section':
      input = <SectionField {...{ field, fieldKey, form, index }} />;
      break;
    case 'file':
      input = <FileField {...{ inputProps, field, form }} />;
      break;
    case 'hidden':
      // we don't actually need to render anything, as useForm will handle submitting the value.
      // however it might be helpful to see it in the HTML while developing.
      // multiple values can just be rendered as a CSV in this case
      try {
        input = <input type="hidden" value={field.multiple ? field.value?.join(',') : field.value} />;
      } catch (err) {
        // something went wrong. it doesn't really matter, but log it anyway
        console.error(err);
        input = null;
      }
  }

  let style = {};
  if (field.hidden) {
    style.display = 'none';
  }

  return <span style={style}>{input}</span>;
}

const Buttons = props => {
  const {
    onClose,
    action,
    submitButtonLabel,
    form
  } = props;

  let btnSize = 'large';

  let buttons = [
    // cancel / reset
    { color: 'secondary', size: btnSize, label: null, onClick: null },

    // submit / save
    { color: 'primary', size: btnSize, label: null, onClick: null }
  ]

  // if editing, then show Save Changes and Reset Fields buttons.
  // otherwise show Submit and Cancel
  if (action === 'update') {
    // for some reason this is rendering twice, with form.changed being
    // false on the 2nd render, when it should be true.
    // enabling the buttons either way for now until this can be figured out
    //let buttonsDisabled = !form.changed;
    let buttonsDisabled = false;

    buttons[0].label = 'Reset Fields';
    buttons[0].onClick = form.resetFields;
    buttons[0].disabled = buttonsDisabled;

    buttons[1].label = 'Save Changes';
    buttons[1].onClick = form.onSubmit;
    buttons[1].disabled = buttonsDisabled;

  } else {
    buttons[0].label = 'Cancel';
    buttons[0].onClick = onClose;

    buttons[1].label = submitButtonLabel || 'Submit';
    buttons[1].onClick = form.onSubmit;
  }

  let sx = {
    boxShadow: 'none',
    textTransform: 'none',
    fontWeight: 600
  }

  return (
    <Box className={classes.buttons} display="flex" justifyContent="space-between">
      {buttons.map(button => ((
        <Button
          key={'button-' + button.label}
          className={classes.button}
          onClick={button.onClick}
          variant="contained"
          color={button.color}
          size={button.size}
          disabled={button.disabled}
          sx={sx}
        >
          {button.label}
        </Button>
      )))}
    </Box>
  );
}

const Form = memo(props => {
  const {
    header,
    fields,
    action,
    onChange,
    onSubmit,
    onClose,
    submitButtonLabel,
    formatParams
  } = props;

  const form = useForm({
    fields,
    onChange,
    onSubmit,
    onClose,
    formatParams
  });

  const {
    clearFormError,
    formError
  } = form;

  let formDivider = null, formSpacer = null;
  if (header) {
    formDivider = <hr />;
    formSpacer = <br />;
  }

  return (
    <div className={classes.form}>
      {formDivider}
      <Typography variant="h6" className={classes.formTitle}>{header}</Typography>
      {formSpacer}
      <div>
        {fields.map((field, i) => {
          let key = 'field-' + i;
          return <FieldInput {...{ key: key, fieldKey: key, index: i, field, form }} />
        })}
        <hr />
        <Buttons {...{
          onClose,
          submitButtonLabel,
          action,
          form
        }} />
      </div>
      <ErrorModal error={formError} onClose={clearFormError} />
    </div>
  );
}, (prevProps, nextProps) => {
  // If there's a change in the "loaded" state of the form,
  // or the number of fields, allow it to be re-rendered
  // Once the form has been completely loaded, i.e. all necessary requests have been made, this func
  // will return true, preventing modal re-renders from re-rendering the form unnecessarily
  //  - example: see discount request in JobConfigForm
  let changed = prevProps.loaded !== nextProps.loaded ||
    prevProps.fields.length !== nextProps.fields.length ||
    prevProps.lastUpdate !== nextProps.lastUpdate;

  return !changed;
});

export default Form;
